import React, { useContext, useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom';
import { formatDateDDMMMYYYY } from 'Common/HelperClass';
import { ChangeIcon, CopyIcon, DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import { ArsContext } from '../ARS/Contexts/ArsContext';
import ChangeOwnerDialog, { User } from './ChangeOwnerDialog';
import { transferOwner } from './services/RuleService';
import { AppContext } from 'Context/AppContext';
import { RuleSortableColumn, SortType } from './enums';
import Table from 'Components/DesignSystem/Table/Table';
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Menu from 'Components/DesignSystem/Menu/Menu';
import Status from 'Components/DesignSystem/Status/Status';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Alert from 'Components/DesignSystem/Alert/Alert';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { TableColumn } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';

/**
 * The Compatibility list grid on the DS `Table` (§ 107), same recipe as the Guide
 * models grid (§ 101) and the Lists grid (§ 81) — with one capability those two did
 * not need.
 *
 * **The pinned columns are the point.** Nine columns do not fit a 1440px viewport, so
 * this table scrolls horizontally, and the two things a user needs while scrolling are
 * the rule NAME they are hunting for and the row ACTIONS they want to click. The
 * legacy markup pinned exactly those two by hand — `tw-sticky tw-left-0` on the name
 * `<th>`, `tw-sticky tw-right-0` on the actions `<th>`, and `TdElement`'s
 * `sticky`/`stickToRight` flags on the cells. The DS `Table` had no such prop, so a
 * naive migration would have silently dropped the pinning: the table would still
 * work, and nobody reading a diff of a `columns` array would notice it go. Hence
 * `TableColumn.sticky` (§ 106), and hence `sticky: 'left'` / `sticky: 'right'` below.
 *
 * What else changed, and why:
 *
 * - **Sort stays fully controlled and two-state.** `handleSort` here owns the
 *   asc/desc toggle exactly as before, so the DS header's own asc→desc→null cycle
 *   never applies — the suggested direction is ignored and only the column id is
 *   forwarded. The visible gain is that a sortable column is now a real `<button>`
 *   with `aria-sort` on its `<th>`; every header was previously a `<th onClick>`,
 *   unreachable by keyboard. The Active column also stops claiming to be sortable:
 *   legacy put `tw-cursor-pointer` on the shared `headerClasses`, so all nine headers
 *   showed a pointer and only eight did anything.
 *
 * - **The overflow menu is one real menu.** `OverflowOption` rendered
 *   `role="menu"` once PER ITEM — four menus of one item each — on `<Link to="#">`
 *   elements gated by opacity and `pointer-events`, which is not disablement a
 *   screen reader can see. The DS `Menu` is one `role="menu"` of `menuitem`s with
 *   true `disabled` semantics, Escape/outside dismissal via `onDismiss`, and the
 *   § 81 portal-to-body positioning (the table wrapper is an overflow container and
 *   would clip an absolutely-positioned popover).
 *
 * - **The Committed chip is the DS `Status`.** It was a hand-tinted `<div>` +
 *   `<label>` pair carrying `#CFFFCE`/`#11980C` — a green found nowhere in the
 *   palette. Yes → `success`, No → `neutral`, matching the Guide grid's grammar.
 *
 * - **The side-panel note is the DS `Alert`.** It was a `#fffae6`/`#ffc400` div with
 *   a wordless `WarningIcon` and no role, so the warning was invisible to assistive
 *   tech.
 *
 * Deliberately unchanged: every permission gate, the `finally` contract in
 * `onRuleOwnerChange` (close + refetch on success, failure AND throw), the
 * `rules-ruletable-div-1` / `-div-2` testids that are NOT row-unique (pinned as
 * must-not-regress by the suite — one per row, so `getAllByTestId`), and the
 * `newId`/`setRuleList` props the parent still passes.
 */
function RuleTable({ mode, ruleList, loading, handleDelete, handleEditAndCreateVersion, NoDataComponent, handleActiveToggle, isSidePanel, handleClick, fetchTableData, sortColumn, setSortColumn, sortDirection, setSortDirection }: RuleTableProps) {
    const [selectIndex, setSelectIndex] = useState<number | null>(null);
    /* Right-anchored like § 81: the menu hangs leftward from the kebab, which is the
       last column and therefore hard against the right edge. */
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const { model, activeSubmodelIndex } = useContext(ArsContext);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [selectedRule, setSelectedRule] = useState<Rule | null>(null);
    const appData = useContext(AppContext);

    const showActiveColumn = isSidePanel && mode !== 'category_relevancy';

    const rulesArray = useMemo(() => {
        const submodel = model?.submodel[activeSubmodelIndex];
        return mode === 'pure_compatibility'
            ? submodel?.pure_compatibility_rules
            : submodel?.rules;
    }, [mode, model, activeSubmodelIndex]);

    const closeOwnerChangeDialog = () => setShowDialog(false);

    const onClickedChangeOwner = (responseItem: Rule) => {
        setShowDialog(true);
        setSelectedRule(responseItem);
    }

    useEffect(() => {
        /* A fixed-position menu detaches from its row the moment anything scrolls, so
           scrolling closes it. Outside-click and Escape are the DS Menu's own job
           (`onDismiss`) — the legacy `mousedown` listener and `menuRef` are gone. */
        function handleScroll() {
            if (selectIndex !== null) {
                setSelectIndex(null);
            }
        }
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [selectIndex]);

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, defaultIndex: number) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom + 2,
            right: window.innerWidth - rect.right,
        });
        setSelectIndex(prev => (prev === defaultIndex ? null : defaultIndex));
    };

    const handleSort = (column: RuleSortableColumn) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === SortType.ASC ? SortType.DESC : SortType.ASC);
        } else {
            setSortColumn(column);
            setSortDirection(SortType.ASC);
        }
    };

    const deleteFunction = async (rule: Rule) => {
        handleDelete(rule)
    }

    const createPermission = appData?.pm?.checkPermission("gmsrules_create");
    const updatePermission = appData?.pm?.checkPermission("gmsrules_update");
    const deletePermission = appData?.pm?.checkPermission("gmsrules_delete");
    const hasRuleOwnerTransferPermission = appData?.pm?.checkPermission("gmsrules_transfer")

    const getToggleButtonValue = (rule: Rule): boolean => {
        return rulesArray?.includes(rule?.id) ?? false;
    };

    const ruleChangeSuccessMessage = (user: string) => `Rule owner changed to ${user}. They can now edit and manage this rule.`;
    const ruleChangeFailureMessage = "Rule owner could not be changed";

    const onRuleOwnerChange = async (data: TransferOwnerPayload) => {
        try {
            const response = await transferOwner(data)
            if (response?.data?.done)
                toast.success(ruleChangeSuccessMessage(`${data?.username}`));
            else
                toast.error(ruleChangeFailureMessage)
        } catch (error) {
            console.log('Error', error);
            toast.error(ruleChangeFailureMessage)
        }
        finally {
            closeOwnerChangeDialog()
            fetchTableData()
        }
    }

    const renderRowActions = (row: Rule, rowIndex: number) => {
        const menuItems: MenuItem[] = [
            {
                id: 'edit', label: 'Edit', icon: <EditIcon />,
                disabled: !updatePermission,
                'data-testid': `rules-row-${rowIndex}-menu-btn-edit`,
                onClick: () => { setSelectIndex(null); handleEditAndCreateVersion(row, "edit"); },
            },
            {
                id: 'copy', label: 'Copy', icon: <CopyIcon />,
                disabled: !createPermission,
                'data-testid': `rules-row-${rowIndex}-menu-btn-copy`,
                onClick: () => { setSelectIndex(null); handleEditAndCreateVersion(row, "copy"); },
            },
            {
                id: 'change-owner', label: 'Change Owner', icon: <ChangeIcon />,
                disabled: !hasRuleOwnerTransferPermission,
                'data-testid': `rules-row-${rowIndex}-menu-btn-change-owner`,
                onClick: () => { setSelectIndex(null); onClickedChangeOwner(row); },
            },
            {
                id: 'delete', label: 'Delete', icon: <DeleteIcon />,
                disabled: !deletePermission,
                'data-testid': `rules-row-${rowIndex}-menu-btn-delete`,
                onClick: () => { setSelectIndex(null); deleteFunction(row); },
            },
        ];

        return (
            <>
                <button
                    type="button"
                    aria-label="More options"
                    aria-haspopup="menu"
                    aria-expanded={selectIndex === rowIndex}
                    data-testid="rules-ruletable-div-2"
                    id={`rules-row-${rowIndex}-btn-overflow`}
                    className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface-hover-strong"
                    onClick={(e) => handleOverflowClick(e, rowIndex)}
                >
                    <OverFlowIcon />
                </button>
                {selectIndex === rowIndex ? ReactDOM.createPortal(
                    <div
                        id={`rules-row-${rowIndex}-overflow-menu`}
                        data-testid={`rules-row-${rowIndex}-overflow-menu`}
                        className="tw-fixed tw-z-[9999] tw-w-52"
                        style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                    >
                        <Menu
                            variant="compact"
                            items={menuItems}
                            data-testid={`rules-row-${rowIndex}-menu`}
                            onDismiss={() => setSelectIndex(null)}
                        />
                    </div>,
                    document.body
                ) : null}
            </>
        );
    };

    const columns: TableColumn<Rule>[] = [
        {
            id: RuleSortableColumn.NAME,
            header: 'Rule Name',
            accessor: (r) => r?.name ?? '',
            sortable: true,
            sticky: 'left',
            minWidth: '220px',
            render: (_value, row, rowIndex) => (
                <div
                    role="button"
                    data-testid="rules-ruletable-div-1"
                    id={`rules-row-${rowIndex}-name`}
                    className="tw-text-accent-bold hover:tw-underline tw-cursor-pointer tw-truncate"
                    title={row?.name ?? ''}
                    onClick={() => handleClick(row)}
                >
                    {row?.name ?? ""}
                </div>
            ),
        },
        ...(showActiveColumn ? [{
            id: 'active',
            header: 'Active',
            accessor: (r: Rule) => getToggleButtonValue(r),
            align: 'center' as const,
            cellType: 'toggle' as const,
            minWidth: '90px',
            render: (_value: any, row: Rule, rowIndex: number) => (
                <Toggle
                    label=""
                    aria-label={`Active — ${row?.name ?? 'rule'}`}
                    data-testid={`rules-row-${rowIndex}-active-toggle`}
                    id={`radio-group-${rowIndex}`}
                    checked={getToggleButtonValue(row)}
                    onChange={() => handleActiveToggle(row, !getToggleButtonValue(row))}
                />
            ),
        } satisfies TableColumn<Rule>] : []),
        {
            id: RuleSortableColumn.CATEGORY_1,
            header: 'Category 1',
            accessor: (r) => r?.left_category_name ?? '',
            sortable: true,
            minWidth: '150px',
        },
        {
            id: RuleSortableColumn.CATEGORY_2,
            header: 'Category 2',
            accessor: (r) => r?.right_category_name ?? '',
            sortable: true,
            minWidth: '150px',
        },
        {
            id: RuleSortableColumn.TYPE,
            header: 'Type',
            accessor: (r) => r?.rule_type ?? '',
            sortable: true,
            minWidth: '100px',
        },
        {
            id: RuleSortableColumn.CREATED_ON,
            header: 'Created On',
            accessor: (r) => formatDateDDMMMYYYY(r?.created_at) || "",
            sortable: true,
            cellType: 'meta',
            minWidth: '130px',
        },
        {
            id: RuleSortableColumn.UPDATED_BY,
            header: 'Updated By',
            accessor: (r) => (r?.updated_by_username || r?.created_by_username) ?? '',
            sortable: true,
            minWidth: '150px',
        },
        ...(!isSidePanel ? [{
            id: RuleSortableColumn.COMMIT,
            header: 'Committed',
            accessor: (r: Rule) => r?.commit === true,
            sortable: true,
            minWidth: '120px',
            render: (_value: any, row: Rule, rowIndex: number) => (
                <Status
                    tone={row?.commit === true ? 'success' : 'neutral'}
                    label={row?.commit === true ? 'Yes' : 'No'}
                    data-testid={`rules-row-${rowIndex}-committed`}
                />
            ),
        } satisfies TableColumn<Rule>] : []),
        {
            id: RuleSortableColumn.LAST_UPDATED,
            header: 'Last Updated',
            accessor: (r) => formatDateDDMMMYYYY(r?.updated_at) || "",
            sortable: true,
            cellType: 'meta',
            minWidth: '130px',
        },
        ...(!isSidePanel ? [{
            id: 'actions',
            header: '',
            accessor: () => null,
            align: 'right' as const,
            sticky: 'right' as const,
            width: '56px',
            render: (_value: any, row: Rule, rowIndex: number) => renderRowActions(row, rowIndex),
        } satisfies TableColumn<Rule>] : []),
    ];

    return (<>
        {showActiveColumn && (
            <div className="tw-mx-4 tw-my-2">
                <Alert
                    tone="warning"
                    id="rules-side-panel-note"
                    data-testid="rules-side-panel-note"
                >
                    Note: Only committed rule versions are displayed.
                </Alert>
            </div>
        )}
        <div id="rules-table-container" className="tw-mt-3">
            <Table
                /* Bound the grid so <main> stops scrolling the page — see
                   `pageGridHeight.ts` for the measured reserve. `stickyHeader`
                   needs it: sticky resolves against a scrolling ancestor. */
                stickyHeader
                maxHeight={PAGE_GRID_MAX_HEIGHT}
                columns={columns}
                data={ruleList ?? []}
                rowKey="id"
                striped
                hoverable
                loading={loading}
                emptyContent={<NoDataComponent />}
                sortState={{ columnId: sortColumn, direction: sortDirection === SortType.ASC ? 'asc' : 'desc' }}
                onSort={(columnId) => handleSort(columnId as RuleSortableColumn)}
                id="rules-table"
                data-testid="rules-grid"
            />
            {showDialog ? <ChangeOwnerDialog id="rule-change-owner-dialog" dialogHeader={"Change Owner"} labelName={"Select owner"} onClose={() => { closeOwnerChangeDialog(); setSelectedRule(null) }} onSubmit={(user: User) => { if (selectedRule != null) onRuleOwnerChange({ rule_id: selectedRule?.id, userid: user.id, username: `${user?.first_name} ${user?.last_name}` }) }} /> : null}
        </div>
    </>);
}


export default RuleTable
